import {
  AllieAccordion,
  AllieAccordionGroup,
  AllieAlertCircleOutlineIcon,
  AllieButton,
  AllieCheckCircleIcon,
  AllieHeader,
  AllieItem,
  AllieLabel,
  AllieModal,
  AllieText,
  PinCode,
} from '@allie/app-ui-lib';
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthProvider';
import { useAllieAuth } from '../../hooks';
import './ResetPIN.scss';
import { ResetSuccess } from '../ResetSuccess';
import { ResetLoading } from '../ResetLoading';

const LENGTH_PIN = 6;

interface ResetPINProps {
  showLabel: string;
  hideLabel: string;
  id: number;
  modalClose: string;
  modalTitle: string;
  modalBodyTitle: string;
  modalBodySubtitle: string;
  modalBodySubtitleStrong: string;
  validationTitle: string;
  validationNumbersLabel: string;
  validationConsecutiveLabel: string;
  validationRepeatLabel: string;
  accordionTitle: string;
  accordionContentParagraph1: string;
  accordionContentParagraph2: string;
  resetConfirm: string;
  success: {
    modalTitle: string;
    modalBodyTitle: string;
    modalBodySubtitle: string;
    modalConfirm: string;
  };
  loading: {
    modalTitle: string;
    modalBodySubtitle: string;
  };
}

const namespace = 'reset-pin';

export const ResetPIN: React.FC<ResetPINProps> = ({
  showLabel,
  hideLabel,
  id,
  modalClose,
  modalTitle,
  modalBodyTitle,
  modalBodySubtitle,
  modalBodySubtitleStrong,
  validationTitle,
  validationNumbersLabel,
  validationConsecutiveLabel,
  validationRepeatLabel,
  accordionTitle,
  accordionContentParagraph1,
  accordionContentParagraph2,
  resetConfirm,
  success,
  loading,
}) => {
  const { tryGuestLogin } = useContext(AuthContext);
  const { changePin } = useAllieAuth();
  const [newPin, setNewPin] = useState<string>('');
  const [hasNumbers, setHasNumbers] = useState(false);
  const [hasConsecutives, setHasConsecutives] = useState(true);
  const [hasEqual, setHasEqual] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [changePINSuccess, setChangePINSuccess] = useState(false);

  const validatePIN = (userPIN: string) => {
    const patternNumbers = new RegExp('^[0-9]{6}$');
    const patternConsecutive = new RegExp(
      '01|12|23|34|45|56|67|78|89|98|87|76|65|54|43|32|21|10'
    );
    const patternEqual = new RegExp('00|11|22|33|44|55|66|77|88|99');
    setHasNumbers(patternNumbers.test(userPIN));
    setHasConsecutives(
      newPin === '' && newPin.length <= 6 ? true : patternConsecutive.test(userPIN)
    );
    setHasEqual(newPin === '' && newPin.length <= 6 ? true : patternEqual.test(userPIN));
  };

  useEffect(() => {
    validatePIN(newPin);
  }, [newPin]);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      await changePin(newPin, id);
      setIsLoading(false);
      setChangePINSuccess(true);
    } catch (error) {}
  };

  return (
    <AllieModal backdropDismiss={false} isOpen>
      <div className={namespace}>
        {!changePINSuccess && !isLoading && (
          <>
            <AllieHeader className={`${namespace}-header`}>
              <AllieText type="h3">{modalTitle}</AllieText>
              <AllieButton
                corner="rounded-edges"
                allieSize="small"
                allieType="subtle"
                allieColor="neutral"
                onClick={() => tryGuestLogin?.(true)}
              >
                {modalClose}
              </AllieButton>
            </AllieHeader>
            <div className={`${namespace}-body`}>
              <div className={`${namespace}-body-text`}>
                <AllieText type="h4" color="neutral-80">
                  {modalBodyTitle}
                </AllieText>
              </div>
              <div className={`${namespace}-body-text`}>
                <AllieText color="neutral-80">
                  {modalBodySubtitle}
                  <strong>{modalBodySubtitleStrong}</strong>
                </AllieText>
              </div>
              <div className={`${namespace}-pin-code`}>
                <PinCode
                  fields={LENGTH_PIN}
                  onChange={(val) => setNewPin(val)}
                  showLabel={showLabel}
                  hideLabel={hideLabel}
                />
              </div>
              <div className={`${namespace}-body-text`}>
                <AllieText type="body3" color="neutral-50">
                  {validationTitle}
                </AllieText>
                <div
                  className={`${namespace}-input-validation-${hasNumbers ? 'success' : 'failed'}`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body3" color="neutral-50">
                    {validationNumbersLabel}
                  </AllieText>
                </div>
                <div
                  className={`${namespace}-input-validation-${
                    !hasConsecutives ? 'success' : 'failed'
                  }`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body3" color="neutral-50">
                    {validationConsecutiveLabel}
                  </AllieText>
                </div>
                <div
                  className={`${namespace}-input-validation-${!hasEqual ? 'success' : 'failed'}`}
                >
                  <AllieCheckCircleIcon />
                  <AllieText type="body3" color="neutral-50">
                    {validationRepeatLabel}
                  </AllieText>
                </div>
              </div>
            </div>
            <AllieAccordionGroup expand="inset" className={`${namespace}-accordion`}>
              <AllieAccordion>
                <AllieItem className={`${namespace}-accordion-header`} slot="header">
                  <AllieAlertCircleOutlineIcon allieColor="success" />
                  <AllieLabel>{accordionTitle}</AllieLabel>
                </AllieItem>
                <div className={`${namespace}-accordion-content`} slot="content">
                  <AllieText type="body2" color="neutral-40">
                    {accordionContentParagraph1}
                    <br />
                    <br />
                    {accordionContentParagraph2}
                  </AllieText>
                </div>
              </AllieAccordion>
            </AllieAccordionGroup>
            <div className={`${namespace}-confirm`}>
              <AllieButton
                onClick={onSubmit}
                corner="rounded-edges"
                allieColor="primary"
                allieSize="small"
                disabled={!hasNumbers || hasEqual || hasConsecutives}
              >
                {resetConfirm}
              </AllieButton>
            </div>
          </>
        )}
        {changePINSuccess && <ResetSuccess {...success} id={id} />}
        {isLoading && <ResetLoading {...loading} />}
      </div>
    </AllieModal>
  );
};
